/**
 * @file src/pages/authentication-modal/AuthenticationModal.jsx
 *
 * OAuth flow: redirect, same tab — not popup.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import AuthModal from "./components/AuthModal";
import AuthTabs from "./components/AuthTabs";
import OAuthButton from "./components/OAuthButton";
import LoginForm from "./components/LoginForm";
import SignupFormWithOTP from "./components/SignupFormWithOTP";
import FormDivider from "./components/FormDivider";
import LoadingSpinner from "components/ui/LoadingSpinner";

import { useLogin } from "./useLogin";
import { acceptInvitation } from "api/invitation.api";
import { QUERY_KEYS } from "config/queryKeys";
import { ROUTES } from "config/routes";
import { API_BASE_URL, API_PREFIX, BETA_APP_URL } from "config/constants";

const PENDING_INVITE_TOKEN_KEY = "pendingInviteToken";
const MARKETING_SITE_URL = "https://www.auditguardai.com";

const getInitialAuthTab = () => {
  if (typeof window === "undefined") return "signin";

  const tab = new URLSearchParams(window.location.search).get("tab");

  return tab === "signup" ? "signup" : "signin";
};

// ---------------------------------------------------------------------------
// AuthenticationModal
// ---------------------------------------------------------------------------

const AuthenticationModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutate: login, isLoading: isLoginLoading } = useLogin();

  const [activeTab, setActiveTab] = useState(getInitialAuthTab);
  const [isOAuthLoading, setIsOAuthLoading] = useState(false);
  const [isAcceptingInvite, setIsAcceptingInvite] = useState(false);
  const [error, setError] = useState(null);

  const isMarketingHost =
    typeof window !== "undefined" &&
    window.location.hostname === "www.auditguardai.com";

  const redirectToBeta = useCallback((path) => {
    window.location.href = `${BETA_APP_URL}${path}`;
  }, []);

  useEffect(() => {
    setError(null);
  }, [activeTab]);

  const acceptPendingInviteIfPresent = useCallback(async () => {
    const pendingInviteToken = sessionStorage.getItem(PENDING_INVITE_TOKEN_KEY);

    if (!pendingInviteToken) {
      return false;
    }

    setIsAcceptingInvite(true);

    try {
      await acceptInvitation(pendingInviteToken);

      sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);

      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.auth.me() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.companies.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.memberships.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invitations.all });

      return true;
    } catch (err) {
      const status = err?.status || err?.response?.status;
      const message = err?.message || "Failed to accept invitation.";

      if (status === 403) {
        setError("This invitation was sent to a different email address.");
      } else {
        setError(message);
      }

      return false;
    } finally {
      setIsAcceptingInvite(false);
    }
  }, [queryClient]);

  // ── Shared post-auth handler ────────────────────────────────────────────
  const handleAuthSuccess = useCallback(
    async (response) => {
      if (response?.user) {
        queryClient.setQueryData(QUERY_KEYS.auth.me(), response);
      }

      const acceptedPendingInvite = await acceptPendingInviteIfPresent();

      if (acceptedPendingInvite) {
        if (onClose) {
          onClose();
        }

        if (isMarketingHost) {
          redirectToBeta(ROUTES.COMPANY_SELECT);
          return;
        }

        navigate(ROUTES.COMPANY_SELECT, { replace: true });
        return;
      }

      if (sessionStorage.getItem(PENDING_INVITE_TOKEN_KEY)) {
        return;
      }

      if (onClose) {
        onClose();
      }

      let targetRoute = ROUTES.COMPANY_SELECT;

      if (response?.nextStep === "CREATE_COMPANY") {
        targetRoute = ROUTES.COMPANY_ONBOARDING;
      }

      if (response?.nextStep === "SELECT_COMPANY") {
        targetRoute = ROUTES.COMPANY_SELECT;
      }

      if (isMarketingHost) {
        redirectToBeta(targetRoute);
        return;
      }

      navigate(targetRoute, { replace: true });
    },
    [
      acceptPendingInviteIfPresent,
      queryClient,
      navigate,
      onClose,
      isMarketingHost,
      redirectToBeta,
    ],
  );

  // ── Tab change ──────────────────────────────────────────────────────────
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    setError(null);
  }, []);

  // ── Email/password login ────────────────────────────────────────────────
  const handleLogin = useCallback(
    (data) => {
      setError(null);

      login(data, {
        onSuccess: handleAuthSuccess,
        onError: (err) =>
          setError(
            err?.message ?? "Invalid email or password. Please try again.",
          ),
      });
    },
    [login, handleAuthSuccess],
  );

  // ── Signup success ──────────────────────────────────────────────────────
  const handleSignupSuccess = useCallback(
    (response) => {
      handleAuthSuccess(response);
    },
    [handleAuthSuccess],
  );

  // ── Google OAuth — redirect flow ────────────────────────────────────────
  const handleGoogleOAuth = useCallback(() => {
    setIsOAuthLoading(true);
    window.location.href = `${API_BASE_URL}${API_PREFIX}/auth/google`;
  }, []);

  // ── Modal close guard ───────────────────────────────────────────────────
  const handleModalClose = useCallback(() => {
    if (!isLoginLoading && !isOAuthLoading && !isAcceptingInvite) {
      setError(null);
      window.location.replace(MARKETING_SITE_URL);
    }
  }, [isLoginLoading, isOAuthLoading, isAcceptingInvite]);

  const isAnyLoading = isLoginLoading || isOAuthLoading || isAcceptingInvite;

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <AuthModal
      isOpen={isOpen}
      onClose={handleModalClose}
      closeOnBackdrop={!isAnyLoading}
      closeOnEscape={!isAnyLoading}
    >
      <div className="p-6 relative">
        <div className="text-center mb-6">
          <h2
            id="modal-title"
            className="text-2xl font-bold text-gray-900 mb-2"
          >
            Welcome to ZATCA
          </h2>

          <p className="text-gray-600">Access your e-invoicing dashboard</p>
        </div>

        {error && (
          <div
            role="alert"
            className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg"
          >
            <div className="flex items-center gap-2">
              <svg
                className="w-5 h-5 text-red-500 shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>

              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        <AuthTabs activeTab={activeTab} onTabChange={handleTabChange} />

        <div className="space-y-4">
          <OAuthButton
            onClick={handleGoogleOAuth}
            loading={isOAuthLoading}
            disabled={isLoginLoading || isAcceptingInvite}
          />

          <FormDivider text="or" />

          <div className="transition-all duration-300 ease-in-out">
            {activeTab === "signin" ? (
              <LoginForm
                onSubmit={handleLogin}
                loading={isLoginLoading || isAcceptingInvite}
                onSwitchToSignup={() => setActiveTab("signup")}
                disabled={isOAuthLoading || isAcceptingInvite}
              />
            ) : (
              <SignupFormWithOTP
                onSignupSuccess={handleSignupSuccess}
                onSwitchToLogin={() => setActiveTab("signin")}
                disabled={isOAuthLoading || isAcceptingInvite}
              />
            )}
          </div>
        </div>

        {isAnyLoading && (
          <div
            aria-live="polite"
            className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center rounded-lg"
          >
            <div className="text-center">
              <LoadingSpinner size="lg" className="mx-auto mb-3" />

              <p className="text-sm text-gray-600">
                {isOAuthLoading
                  ? "Redirecting to Google..."
                  : isAcceptingInvite
                    ? "Accepting invitation..."
                    : "Authenticating..."}
              </p>
            </div>
          </div>
        )}
      </div>
    </AuthModal>
  );
};

export default AuthenticationModal;
